import { router } from '@inertiajs/react'
import { Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Switch } from '@/components/ui/Switch'
import DeleteButton from '@/components/ui/DeleteButton'
import { BannerPreview } from '@/components/help_center/BannerPreview'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { HelpBannerRow } from '@/pages/HelpCenter/types'

interface HelpBannerListProps {
  banners: HelpBannerRow[]
  categories: { id: string; name: string }[]
  brandPrimary: string | null
  onEdit: (banner: HelpBannerRow) => void
}

function scheduleLabel(banner: HelpBannerRow): string | null {
  const fmt = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  if (banner.starts_at && banner.ends_at) return `${fmt(banner.starts_at)} → ${fmt(banner.ends_at)}`
  if (banner.starts_at) return `From ${fmt(banner.starts_at)}`
  if (banner.ends_at) return `Until ${fmt(banner.ends_at)}`
  return null
}

export function HelpBannerList({ banners, categories, brandPrimary, onEdit }: HelpBannerListProps) {
  const { withAccount } = useRouteParams()

  const targetLabel = (banner: HelpBannerRow) => {
    if (banner.scope === 'all') return 'Whole help center'
    if (banner.scope === 'tagged') return `Tagged ${banner.tags.join(', ')}`
    // The category FK nullifies on delete, so a banner can outlive its target.
    // It renders on nothing in that state, so say so rather than showing a
    // scope that looks live.
    return categories.find(c => c.id === banner.help_category_id)?.name ?? 'Category deleted, pick a new one'
  }

  const toggleActive = (banner: HelpBannerRow, active: boolean) => {
    router.patch(
      routes.helpBanners.update.path(withAccount({ id: banner.id })),
      { help_banner: { active } },
      { preserveScroll: true },
    )
  }

  if (banners.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-sm text-muted-foreground">
          No banners yet. Add one to put a notice above your articles, for example telling readers
          an article covers an older version and where the current one lives.
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      {banners.map(banner => (
        <Card key={banner.id} className="space-y-3 p-4">
          <BannerPreview
            title={banner.title ?? ''}
            bodyHtml={banner.body_html ?? ''}
            tone={banner.tone}
            accentColor={banner.accent_color}
            brandPrimary={brandPrimary}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{targetLabel(banner)}</Badge>
            {scheduleLabel(banner) && <Badge variant="outline">{scheduleLabel(banner)}</Badge>}
            {!banner.dismissible && <Badge variant="outline">Not dismissible</Badge>}

            <div className="ml-auto flex items-center gap-2">
              <Switch
                checked={banner.active}
                onCheckedChange={checked => toggleActive(banner, checked)}
                aria-label={banner.active ? 'Switch this banner off' : 'Switch this banner on'}
              />
              <Button variant="ghost" size="sm" onClick={() => onEdit(banner)}>
                <Pencil className="size-4" /> Edit
              </Button>
              <DeleteButton
                onDelete={() => router.delete(
                  routes.helpBanners.destroy.path(withAccount({ id: banner.id })),
                  { preserveScroll: true },
                )}
              />
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}
